import { useState, useCallback } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import { useClickOutside } from '@/client/hooks/use-click-outside';

export type DateRangePreset = '1h' | '24h' | '7d' | '14d' | '30d' | '90d' | '365d' | 'custom';

interface DateRangeOption {
  id: DateRangePreset;
  label: string;
  days?: number;
  hours?: number;
}

const presets: DateRangeOption[] = [
  { id: '1h', label: 'Last hour', hours: 1 },
  { id: '24h', label: 'Last 24 hours', hours: 24 },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '14d', label: 'Last 14 days', days: 14 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
  { id: '365d', label: 'Last year', days: 365 },
  { id: 'custom', label: 'Custom range' },
];

interface DateRangeSelectorProps {
  value: DateRangePreset;
  onChange: (preset: DateRangePreset, startDate?: string, endDate?: string) => void;
  startDate?: string;
  endDate?: string;
}

export function DateRangeSelector({ value, onChange, startDate, endDate }: DateRangeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(startDate || '');
  const [customEnd, setCustomEnd] = useState(endDate || '');

  // ⚡ Performance Optimization: Use shared click-outside hook instead of manual listener
  const dropdownRef = useClickOutside<HTMLDivElement>(() => {
    setIsOpen(false);
    setShowCustom(false);
  }, isOpen);

  const selectedPreset = presets.find((p) => p.id === value);
  const displayLabel =
    value === 'custom' && startDate && endDate
      ? `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
      : selectedPreset?.label || 'Select range';

  const handlePresetClick = useCallback(
    (preset: DateRangeOption) => {
      if (preset.id === 'custom') {
        setShowCustom(true);
      } else {
        const end = new Date();
        const start = new Date();

        if (preset.hours) {
          // Hour-based presets: calculate start time
          start.setHours(start.getHours() - preset.hours);
        } else if (preset.days) {
          // Day-based presets: calculate start date
          start.setDate(start.getDate() - (preset.days - 1));
        }

        onChange(preset.id, start.toISOString().split('T')[0], end.toISOString().split('T')[0]);
        setIsOpen(false);
        setShowCustom(false);
      }
    },
    [onChange]
  );

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      onChange('custom', customStart, customEnd);
      setIsOpen(false);
      setShowCustom(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-card hover:bg-muted transition-colors text-sm font-medium"
      >
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <span>{displayLabel}</span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-64 rounded-xl border bg-card shadow-xl z-[100] overflow-hidden animate-fade-in">
          {!showCustom ? (
            <div className="p-1">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handlePresetClick(preset)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-colors ${
                    value === preset.id
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <span>{preset.label}</span>
                  {value === preset.id && <Check className="h-4 w-4" />}
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCustom(false)}
                  className="flex-1 px-3 py-2 text-sm rounded-lg border hover:bg-muted transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleCustomApply}
                  disabled={!customStart || !customEnd}
                  className="flex-1 px-3 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md transition-all"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatDateShort(date: string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
